import { t } from 'i18next';
import { z } from 'zod';
import {
  STRONG_PASSWORD_REGEX,
  TWO_FA_TOKEN_REGEX,
  URL_WITH_PROTOCOL_REGEX,
} from '../constants/regex';

// created as functions to ensure translations are available when called
const getEmailSchema = () =>
  z
    .string()
    .min(1, t('validation.email.required'))
    .email(t('validation.email'))
    .max(255, 'maxLength');

const getPasswordSchema = () =>
  z.string().min(1, t('validation.password.required'));

const getRequiredString = () =>
  z.string().min(1, t('general.validation.required'));

const getTokenSchema = () =>
  z.string().regex(TWO_FA_TOKEN_REGEX, t('validation.2fa'));

const getPhoneSchema = () =>
  z
    .string()
    .min(1, t('validation.required'))
    .regex(/^\+?[\d\s()-]{7,20}$/, t('validation.phone.invalid'));

const validateInterval = (from: string | undefined, to: string | undefined) => {
  if (!to || !from) {
    return false;
  }

  const fromHours = Number.parseInt(from.split(':')[0], 10);
  const fromMinutes = Number.parseInt(from.split(':')[1], 10);
  const toHours = Number.parseInt(to.split(':')[0], 10);
  const toMinutes = Number.parseInt(to.split(':')[1], 10);

  return !(
    toHours < fromHours ||
    (toHours === fromHours && toMinutes <= fromMinutes)
  );
};

export const getSignInValidationSchema = () =>
  z.object({
    password: getPasswordSchema(),
    email: getEmailSchema(),
    rememberMe: z.boolean().optional(),
  });

export const get2FATokenValidationSchema = () =>
  z.object({
    token: getTokenSchema(),
  });

export const getForgotPasswordValidationSchema = () =>
  z.object({
    email: getEmailSchema(),
  });

export const getNewMemberValidationSchema = () =>
  z.object({
    email: getEmailSchema(),
    roles: z.array(z.string()).min(1),
    firstName: getRequiredString(),
    lastName: getRequiredString(),
    inboxes: z.array(z.string()).optional(),
  });

export const getInviteUserValidationSchema = () =>
  z.object({
    email: getEmailSchema(),
    firstName: getRequiredString(),
    lastName: getRequiredString(),
    roleIds: z
      .array(z.string())
      .min(1, t('member-settings.validation.roles-required')),
    inboxes: z.array(z.string()),
  });

export const getBulkInviteValidationSchema = () =>
  z.object({
    emails: z.string(),
  });

export const getTimeIntervalValidationSchema = () => {
  const availabilitySchema = z
    .object({
      from: z
        .string()
        .regex(
          /^(?:[01]\d|2[0-3]):[0-5]\d$/,
          t('handover-settings.validation.format'),
        ),
      to: z
        .string()
        .regex(
          /^(?:[01]\d|2[0-3]):[0-5]\d$/,
          t('handover-settings.validation.format'),
        ),
      id: z.string(),
      recurring: z.boolean().optional(),
      include: z.boolean().optional(),
      targetId: z.string(),
      type: z.enum(['agent', 'inbox', 'webclient']),
    })
    .refine(
      ({ from, to }) => {
        return validateInterval(from, to);
      },
      {
        message: t('handover-settings.validation.invalid-interval'),
        path: ['to'],
      },
    );

  return z.object({
    newAvailabilities: z.array(availabilitySchema),
    weekDays: z.array(z.any()).optional(),
    applyToMultiple: z.boolean().optional(),
    isHoliday: z.boolean().optional(),
  });
};

export const validateResetPasswordForm = (
  data: { password: string; passwordCheck: string; token?: string },
  mfaEnabled?: boolean,
) => {
  const errors: Record<string, string> = {};

  if (!data.password) {
    errors.password = t('validation.password.required');
  } else if (!STRONG_PASSWORD_REGEX.test(data.password)) {
    errors.password = t('validation.password.form.invalid');
  }

  if (!data.passwordCheck) {
    errors.passwordCheck = t('validation.password.required');
  } else if (data.password !== data.passwordCheck) {
    errors.passwordCheck = t('validation.password.match');
  }

  if (mfaEnabled && (!data.token || !TWO_FA_TOKEN_REGEX.test(data.token))) {
    errors.token = t('validation.2fa');
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export const validateChangePasswordForm = (
  data: {
    password: string;
    newPassword: string;
    retypePassword: string;
    token?: string;
  },
  mfaEnabled?: boolean,
) => {
  const errors: Record<string, string> = {};

  if (!data.password) {
    errors.password = t('validation.password.required');
  }

  if (!data.newPassword) {
    errors.newPassword = t('validation.password.required');
  } else if (!STRONG_PASSWORD_REGEX.test(data.newPassword)) {
    errors.newPassword = t('validation.password.form.invalid');
  }

  if (!data.retypePassword) {
    errors.retypePassword = t('validation.password.required');
  } else if (data.newPassword !== data.retypePassword) {
    errors.retypePassword = t('validation.password.match');
  }

  if (mfaEnabled && (!data.token || !TWO_FA_TOKEN_REGEX.test(data.token))) {
    errors.token = t('validation.2fa');
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export const getVoiceSettingsValidationSchema = () =>
  z.object({
    agentLanguage: z.string().min(1, t('validation.required')),
    welcomeMessage: z
      .string()
      .min(1, t('validation.required'))
      .refine((value) => value.trim().length > 0, {
        message: t('validation.required'),
      }),
    knowledgeBase: z.string().min(1, t('validation.required')),
    tone: z.string().min(1, t('validation.required')),
    transferDuringOfficeHours: getPhoneSchema(),
    transferOutsideOfficeHours: getEmailSchema(),
  });

// * CKEditor serialises content as HTML even when visually empty (e.g. "<p></p>" or
// * "<p>&nbsp;</p>"), so a plain .min(1) check would pass on an empty editor.
// * Strip all tags and decode &nbsp; before checking whether any visible text remains.
const hasVisibleHtmlContent = (htmlString: string): boolean => {
  const textContent = htmlString
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

  return textContent.length > 0;
};

// * Allow empty value; only validate format when a non-empty value is provided.
const isEmptyOrValidAiKnowledgeUrl = (value: string): boolean => {
  const trimmed = value.trim();

  return !trimmed || URL_WITH_PROTOCOL_REGEX.test(trimmed);
};

export const getTemplateResponseValidationSchema = () =>
  z.object({
    name: z.string().min(1, t('settings.template-response.validation.title')),
    content: z.string().refine(hasVisibleHtmlContent, {
      message: t('settings.template-response.validation.content'),
    }),
    url: z.string().refine(isEmptyOrValidAiKnowledgeUrl, {
      message: t('settings.template-response.validation.url'),
    }),
  });
